/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, User, ArrowLeft, Sparkles } from 'lucide-react';
import axios from 'axios';
import { isDemoMode } from '../services';

interface Persona {
  id: string;
  name: string;
  title: string;
  avatar: string;
  age?: string;
  city?: string;
  occupation?: string;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'persona';
  timestamp: Date;
  personaName?: string;
  personaAvatar?: string;
}

interface PersonaChatProps {
  persona: Persona;
  brandContext?: string;
  onClose?: () => void;
}

const PersonaChat: React.FC<PersonaChatProps> = ({ persona, brandContext, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Add initial greeting when persona changes
    const cityPart = persona.city ? ` from ${persona.city}` : '';
    const agePart = persona.age ? ` I'm ${persona.age} years old` : '';
    const occupationPart = persona.occupation ? ` and work as ${persona.occupation}` : '';
    const greeting = `Hello! I'm ${persona.name}, ${persona.title}${cityPart}.${agePart}${occupationPart}. Feel free to ask me about my brand preferences, shopping habits, or what kind of promotional content appeals to me!`;

    setMessages([{
      id: Date.now().toString(),
      text: greeting,
      sender: 'persona',
      timestamp: new Date(),
      personaName: persona.name,
      personaAvatar: persona.avatar,
    }]);
  }, [persona]);

  const sendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = inputText.toLowerCase();
    setInputText('');
    setIsLoading(true);

    try {
      let responseText: string;

      if (isDemoMode) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 500));

        // Generate contextual mock responses based on keywords
        if (userInput.includes('brand') || userInput.includes('prefer') || userInput.includes('favorite')) {
          responseText = `Great question! I tend to gravitate towards brands that feel authentic and align with my lifestyle. For me, it's not just about the product - it's about what the brand represents. I look for quality, consistency, and brands that understand people like me. ${brandContext ? `When it comes to ${brandContext}, I appreciate their consistent messaging and how they connect with their audience.` : ''}`;
        } else if (userInput.includes('content') || userInput.includes('ad') || userInput.includes('promo')) {
          responseText = `I'm definitely drawn to content that feels genuine rather than overly sales-y. Short video content works really well for me - something I can watch quickly on social media. I also appreciate when brands show real people using their products, not just polished commercials. Humor and relatability go a long way!`;
        } else if (userInput.includes('price') || userInput.includes('cost') || userInput.includes('money')) {
          responseText = `Price is definitely a factor, but it's not everything for me. I'm willing to pay more for quality and brands I trust. That said, I do appreciate good value - promotions and bundle deals catch my attention. What matters most is feeling like I'm getting something worth my money.`;
        } else if (userInput.includes('decide') || userInput.includes('choose') || userInput.includes('buy')) {
          responseText = `My decision process usually starts with recommendations from friends or seeing something on social media. I'll then do some quick research - check reviews and compare options. Brand reputation matters a lot to me. If I've had a good experience before, I'm likely to stick with that brand.`;
        } else if (userInput.includes('social') || userInput.includes('media') || userInput.includes('instagram') || userInput.includes('tiktok')) {
          responseText = `Social media plays a huge role in how I discover brands! I spend a lot of time on Instagram and TikTok, so that's where I see most brand content. Influencer recommendations can be persuasive, but only if they feel genuine. I can usually tell when someone is just promoting for money versus actually using the product.`;
        } else if (userInput.includes('trust') || userInput.includes('loyal')) {
          responseText = `Trust is everything to me. Once a brand earns my trust through consistent quality and good customer service, I become quite loyal. I'm not easily swayed by competitors if I'm happy with what I'm getting. However, if a brand disappoints me or does something I disagree with, it's hard to win me back.`;
        } else {
          responseText = `That's an interesting question! From my perspective as ${persona.title}, I'd say my preferences are shaped by my daily life and values. I appreciate brands that understand their customers and deliver consistent experiences. Is there something specific about my preferences you'd like to know more about?`;
        }
      } else {
        const response = await axios.post(`/api/personas/${persona.id}/chat`, {
          message: inputText,
          context: brandContext ? { brand: brandContext } : undefined,
        });
        responseText = response.data.response;
      }

      const personaMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: responseText,
        sender: 'persona',
        timestamp: new Date(),
        personaName: persona.name,
        personaAvatar: persona.avatar,
      };

      setMessages(prev => [...prev, personaMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I had trouble processing that. Could you try asking differently?',
        sender: 'persona',
        timestamp: new Date(),
        personaName: persona.name,
        personaAvatar: persona.avatar,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    'Why do you prefer your favorite brand?',
    'What type of promotional content catches your attention?',
    'How do you decide which brand to buy?',
    'What role does price play in your purchase decisions?',
  ];

  return (
    <div className="flex flex-col h-full bg-background rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-4">
        <div className="flex items-center space-x-3">
          {onClose && (
            <button
              onClick={onClose}
              className="text-primary-foreground hover:bg-white/10 p-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <span className="text-lg font-semibold text-primary-foreground">{persona.name.charAt(0)}</span>
          </div>
          <div className="text-primary-foreground">
            <h3 className="font-bold text-lg">{persona.name}</h3>
            <p className="text-sm opacity-90">{persona.title}{persona.city ? ` • ${persona.city}` : ''}</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[70%] ${
                message.sender === 'user'
                  ? 'order-2'
                  : 'order-1'
              }`}
            >
              <div className="flex items-start space-x-2">
                {message.sender === 'persona' && (
                  <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-primary">{message.personaName?.charAt(0)}</span>
                  </div>
                )}
                <div
                  className={`rounded-2xl px-4 py-3 ${
                    message.sender === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border border-border text-foreground'
                  }`}
                >
                  {message.sender === 'persona' && (
                    <p className="text-xs text-text-muted mb-1">{message.personaName}</p>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {message.text}
                  </p>
                </div>
                {message.sender === 'user' && (
                  <User className="w-8 h-8 text-primary mt-1" />
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-primary">{persona.name.charAt(0)}</span>
              </div>
              <div className="bg-card border border-border rounded-2xl px-4 py-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce delay-100" />
                  <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce delay-200" />
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Questions */}
      {messages.length === 1 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-text-muted mb-2">Suggested questions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => setInputText(question)}
                className="px-3 py-1 bg-background-secondary hover:bg-background-tertiary text-text-secondary text-xs rounded-full transition-colors border border-border"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border p-4">
        <div className="flex space-x-3">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`Ask ${persona.name} a question...`}
            className="flex-1 bg-background-secondary text-foreground rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary border border-border"
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={!inputText.trim() || isLoading}
            className={`p-3 rounded-xl transition-all ${
              inputText.trim() && !isLoading
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                : 'bg-background-secondary text-text-muted cursor-not-allowed border border-border'
            }`}
          >
            {isLoading ? (
              <Sparkles className="w-5 h-5 animate-pulse" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonaChat;
